import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import api from "../lib/api";
import ZonasHotInventoryTable from "../components/ZonasHotInventoryTable";
import ZonasHotExecutive from "../components/ZonasHotExecutive";
import PdfExportButton from "../components/PdfExportButton";

const MONTH_OPTS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]
  .map((label, i) => ({ value: String(i + 1), label }));

const heat = (v, max) => {
  if (!v) return {};
  const t = Math.min(1, v / Math.max(1, max));
  return { background: `rgba(234, 88, 12, ${0.08 + t * 0.62})`, color: t > 0.55 ? "#fff" : "#7c2d12" };
};

const Kpi = ({ label, value, sub, testid }) => (
  <div className="surface p-4" data-testid={testid}>
    <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-2xl font-black font-display mt-1">{value}</div>
    {sub && <div className="text-[12px] font-semibold text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const Select = ({ label, value, options, onChange, testid }) => (
  <label className="flex flex-col gap-1 text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
    {label}
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] font-bold text-gray-800 normal-case tracking-normal bg-white min-w-[160px]">
      <option value="Todas">(Todas)</option>
      {options.map((o) => (typeof o === "object"
        ? <option key={o.value} value={o.value}>{o.label}</option>
        : <option key={o} value={o}>{o}</option>))}
    </select>
  </label>
);

function Row({ r, months, max, sub, open, onToggle, testid }) {
  return (
    <tr className={sub ? "" : "bg-gray-50 cursor-pointer"} onClick={onToggle} data-testid={testid}>
      <td className={`px-3 py-2 text-[14px] whitespace-nowrap ${sub ? "pl-9 font-semibold text-gray-700" : "font-extrabold"}`}>
        {!sub && (open ? <ChevronDown size={14} className="inline mr-1"/> : <ChevronRight size={14} className="inline mr-1"/>)}
        {sub ? r.salon : r.sucursal}
      </td>
      {months.map((m) => (
        <td key={m.n} className="px-2 py-2 text-center text-[14px] font-bold" style={heat(r.months[m.n], max)}>
          {r.months[m.n] || ""}
        </td>
      ))}
      <td className="px-3 py-2 text-center text-[14px] font-black">{r.total}</td>
      <td className="px-3 py-2 text-[13px] font-semibold text-gray-600 whitespace-nowrap">
        {r.top_model ? `${r.top_model.name} (${r.top_model.n})` : "—"}
      </td>
    </tr>
  );
}

function Details({ title, sub, children, testid, forceOpen }) {
  const [isOpen, setOpen] = useState(false);
  const open = isOpen || forceOpen;
  return (
    <div className="surface overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-orange-50/40 transition-colors" data-testid={`${testid}-toggle`}>
        <span>
          <span className="block text-base md:text-lg font-black font-display">{title}</span>
          <span className="block text-[12px] text-gray-500 font-semibold">{sub}</span>
        </span>
        <span className="flex items-center gap-1 text-[12px] font-extrabold text-orange-600">
          {open ? "Ocultar detalle" : "Ver detalle"} {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
        </span>
      </button>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
}

function Pivot({ d }) {
  const [closed, setClosed] = useState({});
  const max = Math.max(1, ...d.groups.flatMap((g) => g.rows.flatMap((r) => Object.values(r.months))));
  return (
    <div className="overflow-x-auto" data-testid="zonas-hot-pivot">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-[12px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
            <th className="px-3 py-3 text-left font-extrabold">Sucursal / Showroom</th>
            {d.months.map((m) => <th key={m.n} className="px-2 py-3 font-extrabold">{m.label.slice(0, 3)}</th>)}
            <th className="px-3 py-3 font-extrabold">Total</th>
            <th className="px-3 py-3 text-left font-extrabold">Modelo top</th>
          </tr>
        </thead>
        <tbody>
          {d.groups.map((g) => (
            <Fragment key={g.sucursal}>
              <Row r={g} months={d.months} max={max * 2} open={!closed[g.sucursal]}
                   onToggle={() => setClosed((c) => ({ ...c, [g.sucursal]: !c[g.sucursal] }))}
                   testid={`zonas-hot-group-${g.sucursal}`}/>
              {!closed[g.sucursal] && g.rows.map((r) => (
                <Row key={r.salon} r={r} months={d.months} max={max} sub testid={`zonas-hot-row-${r.salon}`}/>
              ))}
            </Fragment>
          ))}
          <tr className="border-t-2 border-gray-900" data-testid="zonas-hot-total-row">
            <td className="px-3 py-3 text-[13px] font-black">Total general</td>
            {d.months.map((m) => <td key={m.n} className="px-2 py-3 text-center text-[13px] font-black">{d.month_totals[m.n] || ""}</td>)}
            <td className="px-3 py-3 text-center text-[14px] font-black">{d.total}</td>
            <td className="px-3 py-3 text-[12px] font-bold text-gray-500">Promedio mensual: {d.promedio}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function ZonasHotPage() {
  const [f, setF] = useState({ year: "", mes: "Todas", vendedor: "Todas", marca: "Todas", modelo: "Todas", sucursal: "Todas" });
  const [d, setD] = useState(null);
  const [inv, setInv] = useState(null);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);
  const pageRef = useRef(null);
  useEffect(() => {
    setLoading(true);
    const common = { vendedor: f.vendedor, marca: f.marca, modelo: f.modelo, sucursal: f.sucursal, ...(f.mes !== "Todas" ? { mes: f.mes } : {}) };
    api.get("/zonas-hot/summary", { params: { ...(f.year ? { year: f.year } : {}), ...common } })
      .then((r) => {
        setD(r.data);
        if (r.data.year) {
          api.get("/zonas-hot/inventory-days", { params: { year: r.data.year, ...common } })
            .then((x) => setInv(x.data)).catch(() => setInv(null)).finally(() => setLoading(false));
        } else setLoading(false);
      }).catch(() => { setD({ years: [] }); setLoading(false); });
  }, [f, tick]);

  if (!d) return <div className="p-8 text-gray-500 font-semibold" data-testid="zonas-hot-loading">Cargando…</div>;
  if (!d.years?.length) {
    return (
      <div className="p-8" data-testid="zonas-hot-empty">
        <h1 className="text-3xl font-black font-display">Zonas Hot SDM</h1>
        <p className="text-gray-500 font-semibold mt-2">Aún no hay ventas cargadas. Sube el Excel en <Link to="/admin/integrations" className="text-orange-600 underline">Integraciones · Zonas Hot SDM</Link>.</p>
      </div>
    );
  }
  const topSuc = [...(d.groups || [])].sort((a, b) => b.total - a.total)[0];
  const topDays = (inv?.rows || []).filter((r) => r.sucursal === topSuc?.sucursal && r.dias_al_vender != null);
  const topAvg = topDays.length ? Math.round(topDays.reduce((s, r) => s + r.dias_al_vender, 0) / topDays.length) : null;
  const mesLabel = f.mes !== "Todas" ? `${MONTH_OPTS[+f.mes - 1].label} ` : "";
  const best = [...(d.months || [])].sort((a, b) => (d.month_totals[b.n] || 0) - (d.month_totals[a.n] || 0))[0];
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="zonas-hot-page" ref={pageRef}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-orange-600 text-[11px] font-black uppercase tracking-[0.2em]"><Flame size={14}/> Resumen</div>
          <h1 className="text-4xl font-black font-display mt-1">Zonas Hot SDM</h1>
          <p className="text-[13px] text-gray-500 font-semibold mt-1">Unidades vendidas (cuenta de chasis) por sucursal, showroom y mes.</p>
        </div>
        <div className="flex gap-3 flex-wrap items-end">
          <div className="flex gap-1 flex-wrap" data-testid="zonas-hot-years">
            {d.years.map((y) => (
              <button key={y} onClick={() => setF({ ...f, year: y })} data-testid={`zonas-hot-year-${y}`}
                      className={`px-3 py-2 rounded-full text-[13px] font-extrabold transition-colors ${y === d.year ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                {y}
              </button>
            ))}
          </div>
          <Select label="Mes" value={f.mes} options={MONTH_OPTS} onChange={(v) => setF({ ...f, mes: v })} testid="zonas-hot-mes"/>
          <Select label="Marca" value={f.marca} options={d.marcas} onChange={(v) => setF({ ...f, marca: v, modelo: "Todas" })} testid="zonas-hot-marca"/>
          <Select label="Modelo" value={f.modelo} options={d.modelos || []} onChange={(v) => setF({ ...f, modelo: v })} testid="zonas-hot-modelo"/>
          <Select label="Sucursal" value={f.sucursal} options={d.sucursales || []} onChange={(v) => setF({ ...f, sucursal: v })} testid="zonas-hot-sucursal"/>
          <Select label="Nombre de vendedor" value={f.vendedor} options={d.vendedores} onChange={(v) => setF({ ...f, vendedor: v })} testid="zonas-hot-vendedor"/>
          <button data-pdf-hide onClick={() => setTick((t) => t + 1)} disabled={loading} title="Actualizar datos" data-testid="zonas-hot-refresh-btn"
                  className="h-[38px] px-3 rounded-lg border border-gray-200 bg-white text-[13px] font-extrabold text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors flex items-center gap-2 disabled:opacity-60">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Actualizar
          </button>
          <div data-pdf-hide>
            <PdfExportButton targetRef={pageRef} filename={`zonas-hot-sdm-${d.year}${f.mes !== "Todas" ? `-${f.mes}` : ""}`}
                             title="Zonas Hot SDM" testid="zonas-hot-pdf-btn"
                             subtitle={`${mesLabel}${d.year} · Marca: ${f.marca} · Modelo: ${f.modelo} · Sucursal: ${f.sucursal} · Vendedor: ${f.vendedor}`}
                             beforeCapture={async () => { setPdfMode(true); await new Promise((r) => setTimeout(r, 600)); return () => setPdfMode(false); }}
                             afterCapture={(restore) => restore()}/>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi label={`Unidades ${mesLabel}${d.year}`} value={d.total} testid="zonas-hot-kpi-total"/>
        <Kpi label="Promedio mensual" value={d.promedio} sub={`${d.months.length} meses con ventas`} testid="zonas-hot-kpi-avg"/>
        <Kpi label="Sucursal top" value={topSuc?.sucursal || "—"} sub={topSuc ? `${topSuc.total} unidades` : ""} testid="zonas-hot-kpi-suc"/>
        <Kpi label={`Días prom. inventario · ${topSuc?.sucursal || "—"}`} value={topAvg != null ? `${topAvg} días` : "—"}
             sub={topDays.length ? `al facturar · ${topDays.length} unidades con dato` : "sin dato de inventario"} testid="zonas-hot-kpi-top-days"/>
        <Kpi label="Mejor mes" value={best?.label || "—"} sub={best ? `${d.month_totals[best.n]} unidades` : ""} testid="zonas-hot-kpi-month"/>
      </div>
      {d.total ? (
        <>
          <ZonasHotExecutive d={d} inv={inv} onMonthClick={(m) => setF({ ...f, mes: f.mes === String(m) ? "Todas" : String(m) })}/>
          <Details title="Detalle mensual por sucursal y showroom" sub="Réplica de la hoja Resumen, con modelo top" testid="zonas-hot-details-pivot" forceOpen={pdfMode}>
            <Pivot d={d}/>
          </Details>
          <Details title="Días en inventario al facturar" sub="Por chasis, con promedios por sucursal y modelo" testid="zonas-hot-details-inventory" forceOpen={pdfMode}>
            <ZonasHotInventoryTable d={inv} full={pdfMode}/>
          </Details>
        </>
      ) : <div className="surface p-8 text-center text-gray-500 font-semibold" data-testid="zonas-hot-no-rows">Sin ventas para estos filtros.</div>}
    </div>
  );
}
